import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { LoadingSpinner } from "./LoadingSpinner";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "social";
  size?: "xs" | "sm" | "md" | "lg";
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      loading = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const baseStyles =
      "inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

    const variants = {
      primary: "bg-primary text-text-inverse hover:bg-primary-hover focus:ring-primary",
      secondary: "bg-strong text-text-inverse hover:bg-border-strong focus:ring-fill-muted",
      outline:
        "border border-border-primary bg-bg-primary text-text-primary hover:bg-bg-secondary focus:ring-primary",
      ghost: "text-text-primary hover:bg-bg-tertiary focus:ring-primary",
      social:
        "border border-border-secondary bg-bg-primary text-text-primary hover:bg-bg-secondary focus:ring-fill-muted shadow-sm",
    };

    const sizes = {
      xs: "px-2.5 py-1.5 text-xs",
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2.5 text-sm",
      lg: "px-5 py-3 text-base",
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], fullWidth && "w-full", className)}
        disabled={loading || props.disabled}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <LoadingSpinner size="sm" variant="white" />
            <span>{t("common.loading")}</span>
          </div>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
